// World-create v2 (M24). Pure data + helpers for the create screen's flat-preset
// picker and its game-rules toggles. A flat preset is realised without any
// generator change: the flat generator fills top/filler/base from ids.grass/dirt/
// stone, so boot just remaps those three ids to the preset's blocks. The game-rule
// toggles map to the five rules the engine actually honours.

export interface FlatPreset {
  readonly id: string;
  readonly label: string;
  readonly top: string; // surface block (→ ids.grass)
  readonly filler: string; // the two blocks below (→ ids.dirt)
  readonly base: string; // everything down to bedrock (→ ids.stone)
}

export const FLAT_PRESETS: readonly FlatPreset[] = [
  { id: 'classic', label: 'Classic Flat', top: 'grass_block', filler: 'dirt', base: 'stone' },
  { id: 'desert', label: 'Desert', top: 'sand', filler: 'sand', base: 'sandstone' },
  { id: 'snowy', label: 'Snowy Tundra', top: 'snow_block', filler: 'dirt', base: 'stone' },
  { id: 'stone', label: 'Bare Stone', top: 'stone', filler: 'stone', base: 'stone' },
];

/** The block trio for a preset id, defaulting to Classic Flat for unknown ids. */
export function flatPresetBlocks(id: string | undefined): { top: string; filler: string; base: string } {
  const p = FLAT_PRESETS.find((x) => x.id === id) ?? FLAT_PRESETS[0]!;
  return { top: p.top, filler: p.filler, base: p.base };
}

export interface GameRuleToggle {
  readonly id: string;
  readonly label: string;
  readonly default: boolean;
}

/** The game rules the engine reads (world.ts / modes.ts), as create-screen toggles. */
export const GAMERULE_TOGGLES: readonly GameRuleToggle[] = [
  { id: 'keepInventory', label: 'Keep Inventory', default: false },
  { id: 'doDaylightCycle', label: 'Day/Night Cycle', default: true },
  { id: 'doWeatherCycle', label: 'Weather Cycle', default: true },
  { id: 'doMobSpawning', label: 'Mob Spawning', default: true },
  { id: 'mobGriefing', label: 'Mob Griefing', default: true },
  // REP-1: when on, the session records an input-only `.bbreplay` stream (off by
  // default — recording is opt-in and adds a record dot to the HUD, UI-4).
  { id: 'recordReplay', label: 'Record Replay', default: false },
];

/** Default game-rule map ('true'/'false' strings), the create screen's starting state. */
export function defaultGameRules(): Record<string, string> {
  return Object.fromEntries(GAMERULE_TOGGLES.map((g) => [g.id, String(g.default)]));
}
